import React, {Component, ReactElement, ReactNode} from 'react'
import {CASH_PAYMENT_STATUS, CodeMasterInterface, MESSAGE_TYPE, PAYMENT_TYPE} from '../../../CodeMaster'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {formatDateZeroPadding} from '../../../formatters/formatDate'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {Messages} from '../../../Messages'
import NatFacilityReserveRepo from './repository/NatFacilityReserveRepo'
import {NatGetAdditionalBilling} from './repository/NatGetAdditionalBilling'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import {NatDeleteAdditionalBilling} from './repository/NatDeleteAdditionalBilling'
import {Location} from 'react-router-dom'

interface Props {
    facilityReserveRepo: NatFacilityReserveRepo
    facilityReserveId: string
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    facilityReserveId: number
    reserveTitle: string
    facilityName: string
    userRidgeName: string
    userRoomNumber: string
    userFamilyName: string
    userFirstName: string
    adminFamilyName: string
    adminFirstName: string
    reserveStartDatetime: string
    facilityUseCharge: number
    paymentMethod: string
    cashPaymentStatus: string
    isLoadCompleted: boolean
}


class NatAdminAdditionalBillingPage extends Component<Props & AdminPagePropsSet, States> {

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            facilityReserveId: 0,
            reserveTitle: '',
            facilityName: '',
            userRidgeName: '',
            userRoomNumber: '',
            userFamilyName: '',
            userFirstName: '',
            adminFamilyName: '',
            adminFirstName: '',
            reserveStartDatetime: '',
            facilityUseCharge: 0,
            paymentMethod: '',
            cashPaymentStatus: '',
            isLoadCompleted: false,
        }
        this.backAdditionalBillingListLinkClick = this.backAdditionalBillingListLinkClick.bind(this)
        this.editAdditionalBillingLinkClick = this.editAdditionalBillingLinkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this)
        this.deletAdditionalBilling = this.deletAdditionalBilling.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const responce: Nullable<NatGetAdditionalBilling> = await this.props.asyncCallFunction(
                this.props.facilityReserveRepo.getAdditionalBilling.bind(
                    this.props.facilityReserveRepo, this.props.facilityReserveId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (responce && responce.details) {
                this.setState({
                    facilityReserveId: responce.details.facilityReserveId,
                    reserveTitle: responce.details.reserveTitle,
                    facilityName: responce.details.facilityName,
                    userRidgeName: responce.details.userRidgeName,
                    userRoomNumber: responce.details.userRoomNumber,
                    userFamilyName: responce.details.userFamilyName,
                    userFirstName: responce.details.userFirstName,
                    adminFamilyName: responce.details.adminFamilyName,
                    adminFirstName: responce.details.adminFirstName,
                    reserveStartDatetime: responce.details.reserveStartDatetime,
                    facilityUseCharge: responce.details.facilityUseCharge,
                    paymentMethod: responce.details.paymentMethod,
                    cashPaymentStatus: responce.details.cashPaymentStatus,
                    isLoadCompleted: true,
                })
            }
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
        this.props.eventEmitter.closeLoading()
    }

    backAdditionalBillingListLinkClick(): void {
        this.props.navigate(`/admin/additionalBillingList`)
    }

    editAdditionalBillingLinkClick(): void {
        this.props.navigate(`/admin/additionalBillingEdit/${this.props.facilityReserveId}`)
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleDeleteOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()

        try {
            const ret: Nullable<NatDeleteAdditionalBilling> = await this.props.asyncCallFunction(
                this.props.facilityReserveRepo.deleteAdditionalBilling.bind(
                    this.props.facilityReserveRepo, this.props.facilityReserveId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (ret && ret.requestError === null) {
                this.props.navigate(
                    `/admin/additionalBillingList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0006.toString('追加課金', this.state.reserveTitle),
                            },
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async deletAdditionalBilling(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString(this.state.reserveTitle),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkClick,
                    },
                ],
            },
        )
    }

    renderPaymentType(): ReactNode {
        let paymentType: Nullable<CodeMasterInterface> = null
        let cashPaymentStatus: Nullable<CodeMasterInterface> = null

        try {
            paymentType = PAYMENT_TYPE.codeToEnumConstants(this.state.paymentMethod)
            cashPaymentStatus = CASH_PAYMENT_STATUS.codeToEnumConstants(this.state.cashPaymentStatus)
        } catch (e) {
            cashPaymentStatus = null
        }

        if (this.state.paymentMethod !== PAYMENT_TYPE.CASH.value) {
            return (
                <>
                    <TableRow>
                        <TableRowName>支払方法</TableRowName>
                        <TableCell colSpan={4}>
                            {(paymentType) && paymentType.label}
                        </TableCell>
                    </TableRow>
                </>
            )
        } else {
            return (
                <>
                    <TableRow>
                        <TableRowName>支払方法</TableRowName>
                        <TableCell>
                            {(paymentType) && paymentType.label}
                        </TableCell>
                        <TableRowName>現金支払ステータス</TableRowName>
                        <TableCell>
                            {(cashPaymentStatus) && cashPaymentStatus.label}
                        </TableCell>
                    </TableRow>
                </>
            )
        }
    }


    renderAdditionalBilling(): ReactNode {
        return (
            <>
                <Table>
                    <StyledTableCaption>追加課金情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>タイトル</TableRowName>
                            <TableCell colSpan={4}>
                                {this.state.reserveTitle}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>施設名</TableRowName>
                            <TableCell colSpan={4}>
                                {this.state.facilityName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>部屋番号</TableRowName>
                            <TableCell colSpan={4}>
                                {this.state.userRidgeName} {this.state.userRoomNumber}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>氏名</TableRowName>
                            <TableCell colSpan={4}>
                                {this.state.userFamilyName} {this.state.userFirstName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>管理者確認用氏名</TableRowName>
                            <TableCell colSpan={4}>
                                {this.state.adminFamilyName} {this.state.adminFirstName}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>利用日</TableRowName>
                            <TableCell colSpan={4}>
                                {formatDateZeroPadding(this.state.reserveStartDatetime)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>金額</TableRowName>
                            <TableCell colSpan={4}>
                                {formatNumberWithCommas(Number(this.state.facilityUseCharge))}円
                            </TableCell>
                        </TableRow>
                        {this.renderPaymentType()}
                    </TableBody>
                </Table>
            </>
        )
    }

    renderPageHeaderArea(): Nullable<ReactNode> {
        return (
            <>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ADD_CHARGE}>
                    <PageFotterAreaRight>
                        <DeleteLink
                            onClick={this.deletAdditionalBilling}>
                            追加課金を削除する
                        </DeleteLink>
                    </PageFotterAreaRight>
                    <PageFotterAreaLeft>
                        <AdminPrimaryButton
                            onClick={this.editAdditionalBillingLinkClick}>
                            編集する
                        </AdminPrimaryButton>
                    </PageFotterAreaLeft>
                </AuthorityChecker>
            </>
        )
    }

    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={this.backAdditionalBillingListLinkClick}
                    >
                        追加課金検索へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <Box mb={'15px'}>
                    <PageFooterArea>
                        {this.renderPageHeaderArea()}
                    </PageFooterArea>
                </Box>
                <Box mb={'15px'}>
                    <PageMainArea>
                        {this.renderAdditionalBilling()}
                    </PageMainArea>
                </Box>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminAdditionalBillingPage)
